import { useFetch } from "@/hooks/useFetch";
import { PageHeader, Panel, Loading, StatCard, MockBanner } from "@/components/common";
import { Gauge, Warning } from "@phosphor-icons/react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";

const tooltipStyle = { background: "#121212", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff", fontSize: 12 };
const COLORS = ["#00E5FF", "#FF007F", "#00FF9D", "#FFB800", "#8B5CF6", "#FF3366", "#38BDF8"];

export default function CostControl() {
  const { data, loading } = useFetch("/costs");
  if (loading || !data) return <Loading />;
  const { budgets, spend, by_type, per_episode, estimate_full_episode } = data;

  const budgetRows = [
    ["Daily", spend.daily, budgets.daily], ["Weekly", spend.weekly, budgets.weekly], ["Monthly", spend.monthly, budgets.monthly],
  ];

  return (
    <div>
      <PageHeader icon={Gauge} title="AI Cost Control"
        subtitle="Track every AI cost — per episode, per scene, per generation type. Expensive operations show an estimated cost and require confirmation." />
      <MockBanner>Costs reflect estimated AI usage. Video & voice are $0 until those providers connect; text & image reflect real Universal Key usage patterns.</MockBanner>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {budgetRows.map(([label, used, cap]) => {
          const pct = Math.min(100, Math.round((used / cap) * 100));
          const over = pct > 90;
          return (
            <Panel key={label} data-testid={`budget-${label}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-wider text-zinc-500">{label} budget</span>
                {over && <Warning size={16} weight="fill" className="text-[#FF3366]" />}
              </div>
              <div className="font-display text-2xl font-extrabold text-white">${used.toFixed(2)} <span className="text-sm text-zinc-500 font-normal">/ ${cap}</span></div>
              <div className="mt-2 h-2 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: over ? "#FF3366" : "linear-gradient(90deg,#00E5FF,#FF007F)" }} />
              </div>
            </Panel>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <Panel>
          <div className="text-sm text-zinc-400 mb-4">Cost by generation type</div>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={by_type.filter((d) => d.cost > 0)} dataKey="cost" nameKey="type" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3}>
                {by_type.filter((d) => d.cost > 0).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} stroke="none" />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-3 justify-center mt-2">
            {by_type.map((d, i) => (
              <span key={d.type} className="flex items-center gap-1.5 text-xs text-zinc-400"><span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{d.type} ${d.cost.toFixed(1)}</span>
            ))}
          </div>
        </Panel>
        <Panel>
          <div className="text-sm text-zinc-400 mb-4">Cost per episode</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={per_episode} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis type="number" stroke="#52525B" fontSize={12} /><YAxis type="category" dataKey="episode" stroke="#52525B" fontSize={10} width={110} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Bar dataKey="cost" fill="#00E5FF" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <Panel className="flex items-center gap-4">
        <Gauge size={28} weight="duotone" className="text-[#FFB800]" />
        <div>
          <div className="text-sm text-white font-medium">Estimated cost per full episode (once connected)</div>
          <div className="text-xs text-zinc-400">${estimate_full_episode.low}–${estimate_full_episode.high} · {estimate_full_episode.note}</div>
        </div>
      </Panel>
    </div>
  );
}
